import React from 'react';
import PropTypes from 'prop-types';

const ResumenEstadisticas = ({ resumen = null }) => {
  const formatTime = (hours) => {
    if (typeof hours !== 'number' || hours < 0) return 'N/A';
    
    if (hours === 0) {
      return '0 min';
    } else if (hours < 1) {
      // Menos de 1 hora: mostrar en minutos
      const minutes = Math.round(hours * 60);
      return `${minutes} min`;
    } else if (hours < 24) {
      // Menos de 24 horas: mostrar horas con minutos
      const totalMinutes = Math.round(hours * 60);
      const hoursPart = Math.floor(totalMinutes / 60);
      const minutesPart = totalMinutes % 60;
      
      if (minutesPart === 0) {
        return hoursPart === 1 ? '1 hora' : `${hoursPart} horas`;
      } else {
        return `${hoursPart}h ${minutesPart}min`;
      }
    } else {
      // 24 horas o más: mostrar días, horas y minutos
      const totalMinutes = Math.round(hours * 60);
      const days = Math.floor(totalMinutes / (24 * 60));
      const remainingMinutes = totalMinutes % (24 * 60);
      const hoursPart = Math.floor(remainingMinutes / 60);
      const minutesPart = remainingMinutes % 60;
      
      let result = '';
      if (days > 0) {
        result += days === 1 ? '1 día' : `${days} días`;
      }
      if (hoursPart > 0) {
        if (result) result += ', ';
        result += hoursPart === 1 ? '1 hora' : `${hoursPart} horas`;
      }
      if (minutesPart > 0) {
        if (result) result += ', ';
        result += `${minutesPart} min`;
      }
      
      return result || '0 min';
    }
  };

  const formatPercentage = (percentage) => {
    if (typeof percentage !== 'number' || percentage < 0) return 'N/A';
    return `${percentage}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getProgressBarClass = (efficiency) => {
    if (typeof efficiency !== 'number') return 'bg-secondary';
    
    if (efficiency >= 90) return 'bg-success';
    if (efficiency >= 70) return 'bg-warning';
    return 'bg-danger';
  };

  const getCompletionPercentage = () => {
    if (!resumen || typeof resumen.procesosCompletados !== 'number' || typeof resumen.procesosTotales !== 'number') {
      return 0;
    }
    
    if (resumen.procesosTotales === 0) return 0;
    
    return Math.round((resumen.procesosCompletados / resumen.procesosTotales) * 100);
  };

  if (!resumen) {
    return (
      <div className="text-center py-4" data-testid="resumen-estadisticas">
        <i className="fas fa-info-circle fa-2x text-muted mb-3" />
        <p className="text-muted">No hay datos de resumen disponibles</p>
      </div>
    );
  }

  const completionPercentage = getCompletionPercentage();

  return (
    <div data-testid="resumen-estadisticas">
      <div className="row g-3">
        {/* Tiempo Total */}
        <div className="col-md-6 col-lg-4">
          <div className="card h-100" data-testid="metric-card-tiempo-total">
            <div className="card-body text-center">
              <i 
                className="fas fa-clock fa-2x text-primary mb-2" 
                data-testid="tiempo-total-icon"
              />
              <h6 className="card-title">Tiempo Total</h6>
              <h4 className="text-primary">{formatTime(resumen.tiempoTotal)}</h4>
            </div>
          </div>
        </div>

        {/* Procesos Completados */}
        <div className="col-md-6 col-lg-4">
          <div className="card h-100" data-testid="metric-card-procesos">
            <div className="card-body text-center">
              <i 
                className="fas fa-cogs fa-2x text-info mb-2" 
                data-testid="procesos-icon"
              />
              <h6 className="card-title">Procesos Completados</h6>
              <h4 className="text-info">
                {resumen.procesosCompletados || 0} / {resumen.procesosTotales || 0}
              </h4>
              <div className="progress mt-2" style={{ height: '8px' }}>
                <div
                  className={`progress-bar ${getProgressBarClass(resumen.eficienciaPromedio)}`}
                  role="progressbar"
                  style={{ width: `${completionPercentage}%` }}
                  aria-valuenow={completionPercentage}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Eficiencia Promedio */}
        <div className="col-md-6 col-lg-4">
          <div className="card h-100" data-testid="metric-card-eficiencia">
            <div className="card-body text-center">
              <i 
                className="fas fa-chart-line fa-2x text-success mb-2" 
                data-testid="eficiencia-icon"
              />
              <h6 className="card-title">Eficiencia Promedio</h6>
              <h4 className="text-success">{formatPercentage(resumen.eficienciaPromedio)}</h4>
            </div>
          </div>
        </div>

        {/* Tiempo de Creación */}
        <div className="col-md-6 col-lg-4">
          <div className="card h-100" data-testid="metric-card-creacion">
            <div className="card-body text-center">
              <i 
                className="fas fa-calendar-plus fa-2x text-warning mb-2" 
                data-testid="creacion-icon"
              />
              <h6 className="card-title">Tiempo de Creación</h6>
              <h4 className="text-warning">{formatTime(resumen.tiempoCreacion)}</h4>
              <small className="text-muted">
                {formatDate(resumen.fechaCreacion)}
              </small>
            </div>
          </div>
        </div>

        {/* Tiempo de Ejecución */}
        <div className="col-md-6 col-lg-4">
          <div className="card h-100" data-testid="metric-card-ejecucion">
            <div className="card-body text-center">
              <i 
                className="fas fa-play-circle fa-2x text-danger mb-2" 
                data-testid="ejecucion-icon"
              />
              <h6 className="card-title">Tiempo de Ejecución</h6>
              <h4 className="text-danger">{formatTime(resumen.tiempoEjecucion)}</h4>
              <small className="text-muted">
                {formatDate(resumen.fechaInicio)} - {formatDate(resumen.fechaFinalizacion)}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Resumen adicional */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">
                <i className="fas fa-info-circle me-2" />
                Resumen de Rendimiento
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-1">
                    <strong>Progreso de Completado:</strong> {completionPercentage}%
                  </p>
                  <p className="mb-1">
                    <strong>Eficiencia General:</strong> {formatPercentage(resumen.eficienciaPromedio)}
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1">
                    <strong>Duración Total:</strong> {formatTime(resumen.tiempoTotal)}
                  </p>
                  <p className="mb-0">
                    <strong>Procesos Pendientes:</strong> {(resumen.procesosTotales || 0) - (resumen.procesosCompletados || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ResumenEstadisticas.propTypes = {
  /** Datos de resumen de estadísticas */
  resumen: PropTypes.shape({
    tiempoTotal: PropTypes.number,
    procesosCompletados: PropTypes.number,
    procesosTotales: PropTypes.number,
    eficienciaPromedio: PropTypes.number,
    tiempoCreacion: PropTypes.number,
    tiempoEjecucion: PropTypes.number,
    fechaCreacion: PropTypes.string,
    fechaInicio: PropTypes.string,
    fechaFinalizacion: PropTypes.string
  })
};

ResumenEstadisticas.defaultProps = {
  resumen: null
};

export default ResumenEstadisticas;
